import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Landmark, Lock, Unlock, AlertTriangle } from 'lucide-react';

const denomLabels = [
  { key: 'notes_100', label: '$100', type: 'note', mult: 100 },
  { key: 'notes_50', label: '$50', type: 'note', mult: 50 },
  { key: 'notes_20', label: '$20', type: 'note', mult: 20 },
  { key: 'notes_10', label: '$10', type: 'note', mult: 10 },
  { key: 'notes_5', label: '$5', type: 'note', mult: 5 },
  { key: 'coins_2', label: '$2', type: 'coin', mult: 2 },
  { key: 'coins_1', label: '$1', type: 'coin', mult: 1 },
  { key: 'coins_050', label: '50c', type: 'coin', mult: 0.5 },
  { key: 'coins_020', label: '20c', type: 'coin', mult: 0.2 },
  { key: 'coins_010', label: '10c', type: 'coin', mult: 0.1 },
  { key: 'coins_005', label: '5c', type: 'coin', mult: 0.05 },
];

const emptyDenoms = () => denomLabels.reduce((acc, d) => ({ ...acc, [d.key]: 0 }), {});
function calcTotal(d) { return denomLabels.reduce((sum, dl) => sum + (d[dl.key] || 0) * dl.mult, 0); }

export default function TillTab() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [openDialogVisible, setOpenDialogVisible] = useState(false);
  const [closeDialogVisible, setCloseDialogVisible] = useState(false);
  const [openDenoms, setOpenDenoms] = useState(emptyDenoms());
  const [closeDenoms, setCloseDenoms] = useState(emptyDenoms());
  const [closeNotes, setCloseNotes] = useState('');
  const [historyDialog, setHistoryDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [curRes, histRes] = await Promise.all([api.get('/till/current'), api.get('/till/history')]);
      setCurrent(curRes.data);
      setHistory(histRes.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openTill = async () => {
    try {
      await api.post('/till/open', { denominations: openDenoms });
      toast.success('Till opened');
      setOpenDialogVisible(false);
      setOpenDenoms(emptyDenoms());
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const closeTill = async () => {
    try {
      await api.post('/till/close', { denominations: closeDenoms, notes: closeNotes });
      toast.success('Till closed');
      setCloseDialogVisible(false);
      setCloseDenoms(emptyDenoms());
      setCloseNotes('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const DenomGrid = ({ denoms, setDenoms, testPrefix }) => (
    <div className="space-y-3">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans font-bold mb-2">Notes</p>
        <div className="grid grid-cols-5 gap-2">
          {denomLabels.filter(d => d.type === 'note').map(d => (
            <div key={d.key} className="space-y-1">
              <Label className="text-xs text-zinc-500 font-sans">{d.label}</Label>
              <Input type="number" min="0" value={denoms[d.key]} onChange={e => setDenoms(p => ({ ...p, [d.key]: parseInt(e.target.value) || 0 }))} className="bg-transparent border border-white/10 text-white font-sans text-center h-9" data-testid={`${testPrefix}-${d.key}`} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans font-bold mb-2">Coins</p>
        <div className="grid grid-cols-6 gap-2">
          {denomLabels.filter(d => d.type === 'coin').map(d => (
            <div key={d.key} className="space-y-1">
              <Label className="text-xs text-zinc-500 font-sans">{d.label}</Label>
              <Input type="number" min="0" value={denoms[d.key]} onChange={e => setDenoms(p => ({ ...p, [d.key]: parseInt(e.target.value) || 0 }))} className="bg-transparent border border-white/10 text-white font-sans text-center h-9" data-testid={`${testPrefix}-${d.key}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-white/5">
        <span className="text-zinc-400 font-sans text-sm">Total</span>
        <span className="text-gold font-sans font-semibold text-lg" data-testid={`${testPrefix}-total`}>${calcTotal(denoms).toFixed(2)}</span>
      </div>
    </div>
  );

  return (
    <div>
      {/* Current Till Status */}
      <div className="mb-6 p-4 bg-white/[0.03] border border-white/5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-gold" />
            <h3 className="text-white font-serif text-lg" data-testid="till-status-title">Till Status</h3>
          </div>
          {current ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-0 font-sans" data-testid="till-status-badge">Open</Badge>
          ) : (
            <Badge className="bg-zinc-500/20 text-zinc-400 border-0 font-sans" data-testid="till-status-badge">Closed</Badge>
          )}
        </div>
        {current ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm font-sans">
              <span className="text-zinc-400">Opened by</span>
              <span className="text-white">{current.opener_name}</span>
            </div>
            <div className="flex justify-between text-sm font-sans">
              <span className="text-zinc-400">Opening Total</span>
              <span className="text-gold">${current.opening_total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-sans">
              <span className="text-zinc-400">Opened At</span>
              <span className="text-zinc-300">{new Date(current.opened_at).toLocaleString()}</span>
            </div>
            <Dialog open={closeDialogVisible} onOpenChange={setCloseDialogVisible}>
              <DialogTrigger asChild>
                <Button className="w-full mt-3 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-sm font-sans" data-testid="close-till-trigger"><Lock className="w-4 h-4 mr-2" /> Close Till</Button>
              </DialogTrigger>
              <DialogContent className="bg-bg-secondary border border-white/10 max-w-lg">
                <DialogHeader><DialogTitle className="font-serif text-white">Close Till</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <DenomGrid denoms={closeDenoms} setDenoms={setCloseDenoms} testPrefix="close" />
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Notes / Comments</Label>
                    <Input value={closeNotes} onChange={e => setCloseNotes(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Any variance explanation" data-testid="close-notes-input" />
                  </div>
                  <Button onClick={closeTill} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="confirm-close-till-button">Close Till & Submit</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <Dialog open={openDialogVisible} onOpenChange={setOpenDialogVisible}>
            <DialogTrigger asChild>
              <Button className="w-full bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans" data-testid="open-till-trigger"><Unlock className="w-4 h-4 mr-2" /> Open Till</Button>
            </DialogTrigger>
            <DialogContent className="bg-bg-secondary border border-white/10 max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-white">Open Till - Count Denominations</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <DenomGrid denoms={openDenoms} setDenoms={setOpenDenoms} testPrefix="open" />
                <Button onClick={openTill} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="confirm-open-till-button">Open Till</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* History */}
      <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-3">Till History</h3>
      <div className="space-y-2">
        {history.map(session => (
          <div key={session.id} className={`p-3 bg-white/[0.03] border rounded-sm cursor-pointer card-hover ${session.needs_approval ? 'border-amber-500/20' : 'border-white/5'}`} onClick={() => setHistoryDialog(session)} data-testid={`till-session-${session.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-white font-sans text-sm">{session.opener_name || 'Staff'}</span>
                <span className="text-zinc-600 font-sans text-xs ml-2">{new Date(session.opened_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                {session.needs_approval && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                {session.variance !== undefined && (
                  <span className={`font-mono text-xs ${session.variance > 0 ? 'text-emerald-400' : session.variance < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                    {session.variance >= 0 ? '+' : ''}{session.variance?.toFixed(2)}
                  </span>
                )}
                <Badge className={`${session.status === 'open' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>{session.status}</Badge>
              </div>
            </div>
          </div>
        ))}
        {history.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-till-history">No till sessions yet</p>}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-white">Till Session Details</DialogTitle></DialogHeader>
          {historyDialog && (
            <div className="space-y-3 pt-2 text-sm font-sans">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-zinc-500">Opened By</span><p className="text-white">{historyDialog.opener_name}</p></div>
                <div><span className="text-zinc-500">Opened</span><p className="text-white">{new Date(historyDialog.opened_at).toLocaleString()}</p></div>
                <div><span className="text-zinc-500">Opening Total</span><p className="text-gold">${historyDialog.opening_total?.toFixed(2)}</p></div>
                {historyDialog.closed_at && (
                  <>
                    <div><span className="text-zinc-500">Closed</span><p className="text-white">{new Date(historyDialog.closed_at).toLocaleString()}</p></div>
                    <div><span className="text-zinc-500">Closing Total</span><p className="text-gold">${historyDialog.closing_total?.toFixed(2)}</p></div>
                    <div><span className="text-zinc-500">Expected</span><p className="text-white">${historyDialog.expected_total?.toFixed(2)}</p></div>
                    <div><span className="text-zinc-500">Cash Taken</span><p className="text-white">${historyDialog.cash_taken?.toFixed(2)}</p></div>
                    <div><span className="text-zinc-500">Variance</span><p className={historyDialog.variance > 0 ? 'text-emerald-400' : historyDialog.variance < 0 ? 'text-red-400' : 'text-white'}>{historyDialog.variance >= 0 ? '+' : ''}${historyDialog.variance?.toFixed(2)}</p></div>
                  </>
                )}
              </div>
              {historyDialog.close_notes && <div><span className="text-zinc-500">Notes</span><p className="text-white">{historyDialog.close_notes}</p></div>}
              {historyDialog.needs_approval && <Badge className="bg-amber-500/20 text-amber-400 border-0">Needs Manager Approval</Badge>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
